/**
 * Kanonik manzilni o'qiladigan qismlarga ajratadi (TZ §5.1).
 *
 * `A01-02-03-04`:
 *   A  — korpus
 *   01 — QATOR (koridor)
 *   02 — STELLAJ: ikki ustun orasidagi yarus
 *   03 — QAVAT
 *   04 — shu qavatdagi YACHEYKA
 *
 * Nega kerak: xaritada «A01-03» degan yozuv xodimga hech nima
 * demaydi — u kodni har safar o'zi yechishi kerak bo'lardi.
 * Shu funksiya nomlarni bitta joyda beradi va ular butun
 * interfeysda bir xil o'qiladi.
 */
export interface Address {
  warehouse: string;
  aisle: number;
  rack: number;
  level: number;
  cell: number;
}

const RACK_CODE = /^([A-Z])(\d{2})-(\d{2})-(\d{2})-(\d{2})$/;

export function parseAddress(code: string): Address | null {
  const match = RACK_CODE.exec(code.trim().toUpperCase());
  if (!match) return null;
  return {
    warehouse: match[1]!,
    aisle: Number(match[2]),
    rack: Number(match[3]),
    level: Number(match[4]),
    cell: Number(match[5]),
  };
}

/** «A korpus · 1-qator · 2-stellaj · 3-qavat · 4-yacheyka» */
export function describeAddress(code: string): string {
  const address = parseAddress(code);
  if (!address) return code;
  return [
    `${address.warehouse} korpus`,
    `${address.aisle}-qator`,
    `${address.rack}-stellaj`,
    `${address.level}-qavat`,
    `${address.cell}-yacheyka`,
  ].join(" · ");
}

/** Bo'lak nomi: «A korpus · 1-qator · 3-qavat». */
export function describeSection(section: string): string {
  const match = /^([A-Z])(\d{2})-(\d{2})$/.exec(section.trim().toUpperCase());
  if (!match) return section;
  return `${match[1]} korpus · ${Number(match[2])}-qator · ${Number(match[3])}-qavat`;
}

/**
 * Operatsion va bojxona zonalarining nomlari (TZ §4.5).
 *
 * Kodning o'zi («JOY», «TOL») xodimga hech nima demaydi va ikki
 * bufer bir-biridan faqat uch harf bilan farq qiladi.
 */
export const ZONE_NAMES: Record<string, string> = {
  QAB: "Qabul",
  JOY: "Joylashtirish buferi",
  TOL: "To'ldirish buferi",
  NAZ: "Nazorat",
  OTG: "Jo'natish",
  TAF: "Tafovutni aniqlashtirish",
  BRAK: "Yaroqsiz va sifat tekshiruvi",
  RET: "Qaytarish",
  PRD: "Ishlab chiqarish",
  T: "Bojxona hududi",
  "A-STORAGE": "A korpus — stellaj",
  "B-STORAGE": "B korpus — yerda saqlash",
  "C-STORAGE": "C korpus — pol hududi",
  "VIRT-SARIQ": "Sariq (virtual)",
  "VIRT-QIZIL": "Qizil (virtual)",
  "VIRT-TAFOVUT": "Tafovut hisobi (virtual)",
};

export function zoneName(code: string): string {
  return ZONE_NAMES[code.trim().toUpperCase()] ?? code;
}

/** Qator nomi: «A korpus · 1-qator». */
export function describeAisle(aisle: string): string {
  const match = /^([A-Z])(\d{2})$/.exec(aisle.trim().toUpperCase());
  if (!match) return aisle;
  return `${match[1]} korpus · ${Number(match[2])}-qator`;
}

/**
 * Katak turining NOMINAL o'lchami, sm (TZ §4.2-4.4).
 *
 * Reyestrda o'lcham to'ldirilmagan bo'lsa 3D ko'rinish shu
 * jadvaldan oladi: nolga teng katak chizmada ko'rinmas bo'lib
 * qolardi va qator «teshik» bo'lib ko'rinardi.
 */
export const NOMINAL_CELL_SIZE: Record<string, { length: number; width: number; height: number }> = {
  Y1: { length: 30, width: 100, height: 50 },
  Y2: { length: 55, width: 100, height: 50 },
  PL05: { length: 65, width: 110, height: 75 },
  PL: { length: 80, width: 120, height: 190 },
  P1: { length: 120, width: 80, height: 250 },
  P2: { length: 160, width: 120, height: 250 },
  P3: { length: 240, width: 120, height: 250 },
  P4: { length: 320, width: 120, height: 250 },
  "C-P1": { length: 120, width: 80, height: 220 },
  "C-P2": { length: 190, width: 120, height: 220 },
};

export function cellSize(cellType: string, length: string, width: string, height: string) {
  const given = { length: Number(length), width: Number(width), height: Number(height) };
  const nominal = NOMINAL_CELL_SIZE[cellType.toUpperCase()] ?? { length: 100, width: 100, height: 100 };
  return {
    length: given.length > 0 ? given.length : nominal.length,
    width: given.width > 0 ? given.width : nominal.width,
    height: given.height > 0 ? given.height : nominal.height,
    // Reyestrda bormi — interfeys buni OCHIQ aytadi: taxminiy
    // o'lchamga qarab qaror qabul qilinmasligi kerak.
    measured: given.length > 0 && given.width > 0 && given.height > 0,
  };
}
